import { useEffect, useRef, useState } from 'react';

/** Milliseconds elapsed since `startedAt`, ticking while `running`. */
export function useElapsed(startedAt: number | null, running: boolean, intervalMs = 200): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!running || startedAt == null) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [running, startedAt, intervalMs]);

  if (startedAt == null) return 0;
  return Math.max(0, now - startedAt);
}

/**
 * Milliseconds remaining until `deadline`, ticking while `running`. Fires
 * `onExpire` exactly once when it reaches 0 (only while running). When the round
 * stops, the last value is held — a solved round keeps showing the time it had
 * left rather than snapping to 0. Returns 0 when `deadline` is null.
 */
export function useCountdown(
  deadline: number | null,
  running: boolean,
  onExpire?: () => void,
  intervalMs = 200,
): number {
  const [remaining, setRemaining] = useState(() =>
    deadline == null ? 0 : Math.max(0, deadline - Date.now()),
  );
  const firedRef = useRef(false);
  const onExpireRef = useRef(onExpire);

  useEffect(() => {
    onExpireRef.current = onExpire;
  });

  useEffect(() => {
    firedRef.current = false;
  }, [deadline]);

  useEffect(() => {
    if (deadline == null) {
      setRemaining(0);
      return;
    }
    if (!running) return;
    const update = () => {
      const r = Math.max(0, deadline - Date.now());
      setRemaining(r);
      if (r === 0 && !firedRef.current) {
        firedRef.current = true;
        onExpireRef.current?.();
      }
    };
    update();
    const id = window.setInterval(update, intervalMs);
    return () => window.clearInterval(id);
  }, [deadline, running, intervalMs]);

  return remaining;
}
